// ===========================================
// CreditNova Unified Upload Page
// ===========================================
//
// Route: /workflow/upload
// Purpose: Single page where users upload ALL documents at once,
//          organized into categorized drag-and-drop zones by type.
//
// This page is the core of the concurrent workflow. Instead of
// navigating through 4+ separate per-document pages sequentially,
// users see 1-4 upload zones based on their summary preferences
// and submit everything in one batch.
//
// Behavior:
//   1. Reads summaryPreferences from WorkflowContext to determine
//      which document types the user needs to upload.
//   2. Renders a DocumentUploadZone for each applicable type.
//   3. On "Process All Documents", calls the batch upload API
//      endpoint which creates document records and enqueues
//      BullMQ jobs for concurrent processing.
//   4. After successful upload, updates WorkflowContext with the
//      server-assigned document IDs and navigates to the next step
//      (which will be the processing dashboard in Phase 5).
//
// Used by: apps/web/src/routes/workflow/_layout.tsx (Outlet)
// Depends on:
//   - DocumentUploadZone component (apps/web/src/components/upload/DocumentUploadZone.tsx)
//   - api.documents.uploadBatch (apps/web/src/lib/api-client.ts)
//   - WorkflowContext (apps/web/src/contexts/WorkflowContext.tsx)
//
// ===========================================

import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useWorkflow, type DocumentStatus } from '@/contexts/WorkflowContext';
import { WorkflowNavigation } from '@/components/workflow/WorkflowNavigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  Upload,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Sparkles,
  XCircle,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Search,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api-client';
import {
  DocumentUploadZone,
  type DocumentType,
  type UploadedFile,
} from '@/components/upload/DocumentUploadZone';

// ===========================================
// Route Registration (TanStack Router file-based routing)
// ===========================================

export const Route = createFileRoute('/workflow/_layout/upload')({
  validateSearch: (search: Record<string, unknown>) => ({
    workflowId: (typeof search.workflowId === 'string' ? search.workflowId : undefined) as
      | string
      | undefined,
  }),
  component: UploadStep,
});

// ===========================================
// Types
// ===========================================

// FilesByType tracks the files in each DocumentUploadZone.
// Keys are DocumentType strings; values are arrays of UploadedFile
// objects managed by each zone's internal state.
type FilesByType = Record<DocumentType, UploadedFile[]>;

// Pipeline phase for the processing dashboard after upload.
// idle               — files uploaded, not yet polling
// processing         — SSE streaming, documents being processed by workers
// extracting_fs      — financial statement worker jobs done, extracting FS data
// awaiting_review    — FS extractions ready, user navigated to overrides/metrics
// error              — something failed during FS extraction
//
// Phases after user review (extracting_rest, cross_period, generating, complete)
// are now handled by the dedicated /workflow/generate page.
type PipelinePhase =
  | 'idle'
  | 'processing'
  | 'extracting_fs'
  | 'awaiting_review'
  | 'error';

// DocumentStatus and ActivityEntry types are imported from WorkflowContext

/**
 * Sort financial extractions by fiscal period, most recent first.
 * Extracts the 4-digit year from data.periodEnding (e.g., "December 31, 2024")
 * and sorts descending so [0] is always the latest year.
 */
function sortByLatestPeriod<T extends { data: any; period?: string }>(extractions: T[]): T[] {
  if (extractions.length <= 1) return extractions;
  return [...extractions].sort((a, b) => {
    const yearA = extractYear(a.data?.periodEnding || a.period || '');
    const yearB = extractYear(b.data?.periodEnding || b.period || '');
    return yearB - yearA; // Descending — latest first
  });
}

function extractYear(periodStr: string): number {
  const match = String(periodStr).match(/\b(19|20)\d{2}\b/);
  return match ? parseInt(match[0], 10) : 0;
}

// ===========================================
// Constants
// ===========================================

// Maximum number of files per document type zone.
// This matches the backend batch upload limit of 20 files total
// distributed across up to 4 zones.
const MAX_FILES_PER_ZONE = 12;

// ===========================================
// Module-Level Dedup Guard (API Call Level)
// ===========================================
// Lives outside the component so it survives React Strict Mode's
// unmount/remount cycle in development. This prevents the second
// (surviving) mount from re-sending extraction API calls that the
// first mount already dispatched.
//
// NOTE: The *trigger* guards (fsExtractionTriggered, remainingTriggered)
// are intentionally useRefs, NOT module-level. They need to reset on
// remount so the surviving StrictMode instance can still call the
// extraction functions (which then check extractedDocIdsGlobal to
// skip already-dispatched API calls and navigate as needed).

/** Tracks which document IDs have already been sent to the extraction API. */
const extractedDocIdsGlobal = new Set<string>();

// ===========================================
// Helper: Determine which document types are needed
// ===========================================

// Reads the summary preferences and returns an ordered list of
// DocumentType values that the user selected data points for.
// This drives which upload zones are rendered on the page.
function getRequiredDocumentTypes(
  selectedDataPoints: {
    financialStatements?: string[];
    creditReport?: string[];
    bankStatements?: string[];
    corporateSearch?: string[];
    metrics?: string[];
  } | undefined,
): DocumentType[] {
  if (!selectedDataPoints) return [];

  const types: DocumentType[] = [];

  // Financial statements are needed if any financial data points OR metrics
  // are selected (metrics require financial data for ratio calculations).
  if (
    (selectedDataPoints.financialStatements?.length ?? 0) > 0 ||
    (selectedDataPoints.metrics?.length ?? 0) > 0
  ) {
    types.push('financial_statement');
  }

  if ((selectedDataPoints.creditReport?.length ?? 0) > 0) {
    types.push('credit_report');
  }

  if ((selectedDataPoints.bankStatements?.length ?? 0) > 0) {
    types.push('bank_statement');
  }

  if ((selectedDataPoints.corporateSearch?.length ?? 0) > 0) {
    types.push('corporate_search');
  }

  return types;
}

// ===========================================
// UploadStep Component
// ===========================================

function UploadStep() {
  const { setCurrentStep, updateData, data, markStepComplete, documentStatuses, activityLog, addActivity, setResumedDocumentStatuses } = useWorkflow();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { workflowId: searchWorkflowId } = Route.useSearch();

  // ---------------------------------------------------------------
  // State
  // ---------------------------------------------------------------

  // isUploading: True while the batch upload request is in flight.
  // Disables all zones and the submit button to prevent duplicate uploads.
  const [isUploading, setIsUploading] = useState(false);

  // uploadComplete: True after a successful batch upload.
  // Transitions the page from upload zones to the processing dashboard.
  const [uploadComplete, setUploadComplete] = useState(false);

  // Pipeline phase for the processing dashboard
  const [pipelinePhase, setPipelinePhase] = useState<PipelinePhase>('idle');
  const [expandedIntegrity, setExpandedIntegrity] = useState<Set<string>>(new Set());

  // documentStatuses and activityLog now come from WorkflowContext (SSE managed there)

  // Error message for the pipeline
  const [pipelineError, setPipelineError] = useState<string | null>(null);

  // Whether we're loading a workflow from the backend via ?workflowId= query param
  const [isResuming, setIsResuming] = useState(false);

  // Research consent confirmation modal — shown when user clicks "Process All
  // Documents" and external research is enabled, so they acknowledge the data
  // disclosure before processing begins.
  const [showResearchConfirm, setShowResearchConfirm] = useState(false);

  // Trigger guards — useRef so each StrictMode mount gets its own copy.
  // The surviving mount's useEffect can fire the extraction function;
  // extractedDocIdsGlobal (module-level) prevents duplicate API calls.
  const fsExtractionTriggeredRef = useRef(
    data.workflowState === 'processing_remaining' || data.workflowState === 'financial_review' || data.workflowState === 'metrics_review'
  );
  // Guard to ensure we navigate to /workflow/overrides exactly once.
  // Initialised to true for states that have already passed the overrides
  // stage so the reactive effect below doesn't re-fire on resume.
  const navigatedToOverridesRef = useRef(
    data.workflowState === 'metrics_review' ||
    data.workflowState === 'processing_remaining' ||
    data.workflowState === 'generating' ||
    data.workflowState === 'complete'
  );

  // Seed module-level dedup set with early-extracted doc IDs
  for (const e of (data.earlyExtractions || []) as Array<{ documentId: string }>) {
    extractedDocIdsGlobal.add(e.documentId);
  }

  // Ref to auto-scroll the activity log
  const activityLogRef = useRef<HTMLDivElement | null>(null);
  // Ref to prevent double-click on "Process All Documents" — set synchronously
  // so a second click is blocked before React re-renders with isUploading=true.
  const uploadInProgressRef = useRef(false);

  // filesByType: Controlled state for each DocumentUploadZone.
  // Initialized with empty arrays for each required document type.
  const [filesByType, setFilesByType] = useState<FilesByType>({
    financial_statement: [],
    bank_statement: [],
    credit_report: [],
    corporate_search: [],
  });

  // ---------------------------------------------------------------
  // Derived Values
  // ---------------------------------------------------------------

  // Determine which document type zones to show based on summary preferences
  const requiredTypes = useMemo(
    () => getRequiredDocumentTypes(data.summaryPreferences?.selectedDataPoints),
    [data.summaryPreferences],
  );

  // Total file count across all zones — used for validation and display
  const totalFileCount = useMemo(
    () => requiredTypes.reduce((sum, type) => sum + filesByType[type].length, 0),
    [requiredTypes, filesByType],
  );

  // Check if every required zone has at least one file uploaded.
  // All zones must have files before the user can submit.
  const allZonesHaveFiles = useMemo(
    () => requiredTypes.every((type) => filesByType[type].length > 0),
    [requiredTypes, filesByType],
  );

  // Check if any zone that requires period labels has files without them.
  // Bank statements always require period labels (e.g., "Jan 2025").
  const hasMissingPeriods = useMemo(() => {
    return requiredTypes.some((type) => {
      if (type !== 'bank_statement') return false;
      return filesByType[type].some((f) => !f.period.trim());
    });
  }, [requiredTypes, filesByType]);

  // ---------------------------------------------------------------
  // Resume workflow from ?workflowId= query param
  // ---------------------------------------------------------------
  // When a user clicks a draft analysis from the analyses list (possibly
  // in a different tab), we receive the workflowId as a search param.
  // If the current context doesn't have a workflowId, fetch the workflow
  // from the backend and hydrate the context so the page can render.
  useEffect(() => {
    if (!searchWorkflowId) return;            // No query param
    if (data.workflowId) return;              // Context already has this workflow

    let cancelled = false;
    setIsResuming(true);

    (async () => {
      try {
        const payload = await api.workflows.resume(searchWorkflowId);

        if (cancelled) return;

        // Hydrate WorkflowContext with the backend data.
        // If overridden extractions exist (user already confirmed overrides),
        // use them as the working financialExtractions so the overrides page
        // displays the user's adjusted values if they navigate back to it.
        const rawExtractions = payload.financialExtractions || [];
        const overriddenExtractions = payload.overriddenFinancialExtractions || [];
        const hasOverrides = overriddenExtractions.length > 0;

        const resumedState = payload.workflowState || 'idle';

        updateData({
          workflowId: payload.workflowId,
          analysisId: payload.analysisId,
          workflowState: resumedState,
          summaryPreferences: payload.summaryPreferences || null,
          uploadedDocuments: payload.uploadedDocuments || [],
          financialExtractions: hasOverrides ? overriddenExtractions : rawExtractions,
          overriddenFinancialExtractions: overriddenExtractions,
          earlyExtractions: payload.earlyExtractions || [],
          selectedMetrics: payload.selectedMetrics || [],
          customRatios: payload.customRatios || [],
          offBalanceSheetRecords: payload.offBalanceSheetRecords || {},
        });

        // ---------------------------------------------------------------
        // Fix guard refs for resumed workflows.
        //
        // useRef() only uses its initialiser on the FIRST render, but at
        // that point data.workflowState is still 'idle' (the default).
        // After hydration completes the refs are stale, so we explicitly
        // set them here to prevent re-triggering stages that are already
        // complete (e.g., re-running FS extraction or re-navigating to
        // overrides when the user is past those stages).
        // ---------------------------------------------------------------
        if (['financial_review', 'metrics_review', 'processing_remaining', 'generating', 'complete'].includes(resumedState)) {
          fsExtractionTriggeredRef.current = true;
        }
        if (['metrics_review', 'processing_remaining', 'generating', 'complete'].includes(resumedState)) {
          navigatedToOverridesRef.current = true;
        }

        // If documents were already uploaded, populate document statuses
        if (payload.uploadedDocuments?.length > 0) {
          try {
            const docStatuses = await api.documents.statuses(searchWorkflowId);
            if (!cancelled && docStatuses?.data) {
              setResumedDocumentStatuses(docStatuses.data.map((d: any) => ({
                id: d.id,
                name: d.name || d.id,
                documentType: d.documentType || 'other',
                status: d.status || 'pending',
              })));
            }
          } catch {
            // Non-fatal — statuses will populate via SSE reconnection
          }
        }

        // ---------------------------------------------------------------
        // Route to the correct view based on the resumed workflow state.
        //
        // Backend state is authoritative — state transitions are driven by
        // backend endpoints (upload-batch, document-worker, extraction,
        // report-generation) so the resumed state is always accurate, even
        // if the user refreshed mid-pipeline.
        //
        // 'complete'   → report already generated, go straight to the
        //                 analysis page (no point staying in the workflow).
        // 'generating' → report generation is in-flight on the server,
        //                 show the generating UI and start polling so we
        //                 catch the completion event.
        // Other states → stay on the upload page; SSE reconnects and the
        //                 document status watcher drives the pipeline forward.
        // ---------------------------------------------------------------
        if (cancelled) return;

        if (resumedState === 'complete' && payload.analysisId) {
          // Report already done — navigate directly to the analysis page.
          navigate({ to: '/analyses/$id', params: { id: payload.analysisId } });
          return;
        }

        if (resumedState === 'generating' || resumedState === 'processing_remaining') {
          // Report generation or final processing — handled by the dedicated generate page.
          navigate({ to: '/workflow/generate' });
          return;
        }
      } catch (err: any) {
        if (cancelled) return;
        console.error('Failed to resume workflow:', err);
        toast({
          title: 'Resume Failed',
          description: 'Could not load the workflow. It may have been deleted.',
          variant: 'destructive',
        });
      } finally {
        if (!cancelled) setIsResuming(false);
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWorkflowId]);

  // ---------------------------------------------------------------
  // Sync workflow context step
  // ---------------------------------------------------------------

  useEffect(() => {
    // Map workflowState to the correct StageTracker step so the progress
    // bar reflects the real position when resuming a workflow.
    switch (data.workflowState) {
      case 'financial_review':
        setCurrentStep('overrides');
        break;
      case 'metrics_review':
        setCurrentStep('metrics');
        break;
      default:
        setCurrentStep('upload');
        break;
    }
  }, [setCurrentStep, data.workflowState]);

  // If documents were already uploaded (e.g., user navigated back),
  // show the complete state immediately.
  useEffect(() => {
    if (data.uploadedDocuments && data.uploadedDocuments.length > 0) {
      setUploadComplete(true);
      // If a report was already generated, the generate page handles it
      // (upload.tsx no longer manages the 'complete' phase)
    }
  }, [data.uploadedDocuments, data.reportId]);

  // ---------------------------------------------------------------
  // Reactive navigation: financial_review → overrides
  // ---------------------------------------------------------------
  // In React StrictMode, mount 1's extractFinancialAndNavigateToReview
  // runs the extraction and calls updateData + navigate, but since
  // mount 1 is unmounted the navigate may not take effect. This effect
  // on the surviving mount (mount 2) watches for the workflowState
  // transition and performs the navigation reactively.
  useEffect(() => {
    if (
      !navigatedToOverridesRef.current &&
      data.workflowState === 'financial_review' &&
      data.financialExtractions &&
      data.financialExtractions.length > 0
    ) {
      navigatedToOverridesRef.current = true;
      setPipelinePhase('awaiting_review');
      addActivity('pipeline', 'Navigating to financial review — other documents continue processing', 'info');
      navigate({ to: '/workflow/overrides' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.workflowState, data.financialExtractions]);

  // ---------------------------------------------------------------
  // Reactive navigation: metrics_review → metrics
  // ---------------------------------------------------------------
  // When resuming a workflow where overrides were confirmed but metrics
  // were not yet completed, navigate directly to the metrics page.
  useEffect(() => {
    if (data.workflowState === 'metrics_review') {
      setPipelinePhase('awaiting_review');
      navigate({ to: '/workflow/metrics' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.workflowState]);

  // ---------------------------------------------------------------
  // Processing Pipeline: SSE → Extract → Generate → Navigate
  // ---------------------------------------------------------------

  // addActivity comes from WorkflowContext

  // Auto-scroll activity log when new entries arrive
  useEffect(() => {
    requestAnimationFrame(() => {
      activityLogRef.current?.scrollTo({ top: activityLogRef.current.scrollHeight, behavior: 'smooth' });
    });
  }, [activityLog.length]);

  // Determine whether this workflow includes financial statements
  const hasFinancialDocs = useMemo(
    () => data.uploadedDocuments.some((d) => d.documentType === 'financial_statement'),
    [data.uploadedDocuments],
  );

  // ---------------------------------------------------------------
  // SSE is now managed by WorkflowContext (persists across route
  // navigation). This effect watches documentStatuses from context
  // and triggers pipeline transitions when docs reach terminal states.
  // ---------------------------------------------------------------

  // Set pipeline phase to 'processing' when upload completes.
  // 'uploading' and 'processing' both map to the same visual pipeline phase.
  useEffect(() => {
    if (!uploadComplete || data.reportId) return;
    if (pipelinePhase === 'idle') {
      setPipelinePhase('processing');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadComplete, data.reportId]);

  // Trigger pipeline transitions based on document status changes from context
  useEffect(() => {
    if (!uploadComplete || data.reportId) return;

    // Only consider documents that belong to this workflow's upload batch.
    // This prevents phantom docs from a duplicate upload-batch from
    // polluting the trigger logic.
    const knownDocIds = new Set(data.uploadedDocuments.map((d) => d.id));
    const relevantStatuses = documentStatuses.filter((d) => knownDocIds.has(d.id));

    // --- Financial-first flow ---
    // When financial statements are present, check if ALL FS docs are done.
    if (hasFinancialDocs && !fsExtractionTriggeredRef.current) {
      const fsDocs = relevantStatuses.filter((d) => d.documentType === 'financial_statement');
      const expectedFsCount = data.uploadedDocuments.filter(
        (d) => d.documentType === 'financial_statement'
      ).length;

      if (fsDocs.length >= expectedFsCount && fsDocs.length > 0) {
        const allFsTerminal = fsDocs.every((d) => d.status === 'completed' || d.status === 'failed');
        if (allFsTerminal) {
          fsExtractionTriggeredRef.current = true;
          const completedFs = fsDocs.filter((d) => d.status === 'completed');
          extractFinancialAndNavigateToReview(completedFs);
        }
      }
    }

    // --- No-financial flow ---
    // When no financial statements exist, wait for ALL docs to complete,
    // then navigate to the dedicated generate page.
    if (!hasFinancialDocs) {
      const expectedTotal = data.uploadedDocuments.length;
      const allTerminal = relevantStatuses.length >= expectedTotal && relevantStatuses.length > 0 && relevantStatuses.every(
        (d) => d.status === 'completed' || d.status === 'failed'
      );
      if (allTerminal) {
        navigate({ to: '/workflow/generate' });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentStatuses, uploadComplete, data.reportId, data.workflowState, hasFinancialDocs]);

  // ---------------------------------------------------------------
  // Phase A: Extract Financial Statements → Navigate to Overrides
  //
  // Called when all financial statement worker jobs finish.
  // Extracts each FS document via OpenAI, stores results in
  // WorkflowContext.financialExtractions, then navigates to the
  // overrides page. Other document types continue processing.
  // ---------------------------------------------------------------
  const extractFinancialAndNavigateToReview = async (completedFsDocs: DocumentStatus[]) => {
    setPipelinePhase('extracting_fs');
    addActivity('pipeline', 'Financial statements ready — extracting data for review', 'info');

    try {
      const fsExtractions: Array<{
        documentId: string;
        period: string;
        fileName: string;
        data: any;
      }> = [];

      let skippedByDedup = 0;
      for (const doc of completedFsDocs) {
        // Skip docs that were already extracted (dedup guard).
        // In React StrictMode dev, the first mount already dispatched
        // the API call — the surviving mount should not re-send it.
        if (extractedDocIdsGlobal.has(doc.id)) {
          skippedByDedup++;
          addActivity('extractor', `${doc.name} — already extracted, skipping`, 'info');
          continue;
        }
        extractedDocIdsGlobal.add(doc.id);

        try {
          addActivity('extractor', `Analyzing ${doc.name}...`, 'info');
          const result = await api.extraction.extract({
            uploadId: doc.id,
            workflowId: data.workflowId,
            period: 'annual',
            detailLevel: 'in-depth',
          });
          // Unmask PII tokens for display (DB retains masked data for agents)
          let displayData = result.data;
          if (result.extractionId) {
            try {
              const unmasked = await api.extraction.unmask({
                extractionIds: [result.extractionId],
                data: result.data,
              });
              displayData = unmasked.data as typeof result.data;
            } catch {
              // Non-fatal: fall back to masked data if unmask fails
            }
          }
          // Find period/fileName from uploadedDocuments
          const uploadedDoc = data.uploadedDocuments.find((d) => d.id === doc.id);
          fsExtractions.push({
            documentId: doc.id,
            period: uploadedDoc?.period || 'Annual',
            fileName: uploadedDoc?.fileName || doc.name,
            data: displayData,
          });
          addActivity('extractor', `${doc.name} — extraction complete`, 'success');
        } catch (err) {
          console.error(`Financial extraction failed for ${doc.name}:`, err);
          addActivity('extractor', `${doc.name} — extraction failed`, 'error');
          // Remove from dedup set so it can be retried
          extractedDocIdsGlobal.delete(doc.id);
        }
      }

      if (fsExtractions.length === 0) {
        if (skippedByDedup > 0) {
          // All docs were already dispatched by another invocation
          // (React StrictMode first mount). That invocation will
          // handle updateData + navigate. Exit silently.
          return;
        }
        throw new Error('All financial statement extractions failed');
      }

      // Sort by latest fiscal period so [0] is always the most recent year
      const sortedFsExtractions = sortByLatestPeriod(fsExtractions);

      // Store extractions in WorkflowContext and navigate to overrides
      // Use the latest year's data as the primary for ratio calculations
      // Preserve template-loaded selectedMetrics if already populated;
      // only fall back to summary data-point labels if no metrics were set
      // (e.g. no template was applied).
      updateData({
        financialExtractions: sortedFsExtractions,
        financialData: sortedFsExtractions[0].data,
        financialUploadId: sortedFsExtractions[0].documentId,
        workflowState: 'financial_review',
        ...(data.selectedMetrics.length === 0 ? {
          selectedMetrics: data.summaryPreferences?.selectedDataPoints?.metrics || [],
        } : {}),
      });

      // Await the backend PATCH so the state is persisted before the user
      // can refresh. This is a user-visible transition (navigating to overrides)
      // so the small latency is acceptable.
      try {
        await api.workflows.update(data.workflowId, {
          workflowState: 'financial_review',
          financialExtractions: sortedFsExtractions,
          ...(data.selectedMetrics.length === 0 ? {
            selectedMetrics: data.summaryPreferences?.selectedDataPoints?.metrics || [],
          } : {}),
        });
      } catch (err) {
        console.warn('Failed to sync workflowState=financial_review to backend:', err);
      }

      navigatedToOverridesRef.current = true;
      setPipelinePhase('awaiting_review');
      addActivity('pipeline', 'Navigating to financial review — other documents continue processing', 'info');

      // Navigate to the overrides page
      navigate({ to: '/workflow/overrides' });

    } catch (error: any) {
      console.error('Financial extraction failed:', error);
      setPipelinePhase('error');
      setPipelineError(error.message || 'Failed to extract financial statement data');
      toast({
        title: 'Extraction Failed',
        description: error.message || 'Failed to extract financial data. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // ---------------------------------------------------------------
  // Phase B (Extract Remaining → Cross-Period → Report) is now handled
  // entirely by the dedicated /workflow/generate page. upload.tsx only
  // manages the initial upload + FS extraction + navigation to overrides.
  // ---------------------------------------------------------------

  // ---------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------

  // handleFilesChange: Callback passed to each DocumentUploadZone.
  // Updates the filesByType state for the specific document type zone.
  const handleFilesChange = useCallback(
    (type: DocumentType, files: UploadedFile[]) => {
      setFilesByType((prev) => ({ ...prev, [type]: files }));
    },
    [],
  );

  // Report completion, SSE watchers, and polling are now handled by
  // the dedicated /workflow/generate page.

  // handleProcessAll: Triggered by the "Process All Documents" button.
  // Collects files from all zones, builds the metadata payload, and
  // calls the batch upload API endpoint.
  const handleProcessAll = async () => {
    // Synchronous ref guard — prevents double-click before React re-renders
    if (uploadInProgressRef.current) return;
    uploadInProgressRef.current = true;

    // Guard: Ensure workflow has been initialized (has a real workflowId)
    if (!data.workflowId) {
      uploadInProgressRef.current = false;
      toast({
        title: 'Initializing…',
        description: 'Workflow is still being set up. Please wait a moment and try again.',
      });
      return;
    }

    // Guard: Ensure all zones have files
    if (!allZonesHaveFiles) {
      uploadInProgressRef.current = false;
      toast({
        title: 'Missing Documents',
        description: 'Please upload at least one file in each document category.',
        variant: 'destructive',
      });
      return;
    }

    // Guard: Check for missing period labels on bank statements
    if (hasMissingPeriods) {
      uploadInProgressRef.current = false;
      toast({
        title: 'Missing Period Labels',
        description: 'Please provide a period label (e.g., "Jan 2025") for each bank statement.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);

    try {
      // ---------------------------------------------------------------
      // Build the batch upload payload.
      //
      // The API expects:
      //   files[]    — Array of File objects (multipart form data)
      //   metadata   — JSON string with { workflowId, items[] }
      //
      // items[i] corresponds to files[i] by array index. We iterate
      // through required types in order, collecting files and building
      // the metadata items array in the same sequence.
      // ---------------------------------------------------------------
      const allFiles: File[] = [];
      const metadataItems: Array<{
        documentType: string;
        period: string;
        label: string;
      }> = [];

      for (const type of requiredTypes) {
        for (const uploadedFile of filesByType[type]) {
          allFiles.push(uploadedFile.file);
          metadataItems.push({
            documentType: type,
            period: uploadedFile.period || '',
            label: uploadedFile.label || uploadedFile.file.name,
          });
        }
      }

      // Call the batch upload endpoint
      const result = await api.documents.uploadBatch(allFiles, {
        workflowId: data.workflowId,
        items: metadataItems,
      });

      // ---------------------------------------------------------------
      // Update WorkflowContext with server-assigned document IDs.
      // This data feeds into the processing dashboard (Phase 5) for
      // polling status and displaying progress per document.
      // ---------------------------------------------------------------
      const uploadedDocs = (result.documents || []).map(
        (doc: any, idx: number) => ({
          id: doc.id || doc.documentId,
          documentType: metadataItems[idx]?.documentType || 'unknown',
          period: metadataItems[idx]?.period || '',
          label: metadataItems[idx]?.label || '',
          fileName: allFiles[idx]?.name || '',
        }),
      );

      // The upload-batch endpoint sets workflowState='uploading' on the backend.
      // The document worker will advance to 'processing' when it starts.
      // Set local state to 'uploading' to match — SSE will connect and the
      // document status watcher will drive subsequent transitions.
      updateData({
        uploadedDocuments: uploadedDocs,
        workflowState: 'uploading',
      });

      setUploadComplete(true);
      markStepComplete('upload');

      toast({
        title: 'Upload Complete',
        description: `${allFiles.length} document${allFiles.length !== 1 ? 's' : ''} submitted for processing.`,
      });
    } catch (error: any) {
      // Error is already handled by api-client (toast shown automatically).
      // Reset uploading state so the user can retry.
      console.error('Batch upload failed:', error);
      uploadInProgressRef.current = false; // Allow retry on failure
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload documents. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------

  // Safety guard: workflow must be created on the summary page before
  // arriving here. If workflowId is missing the user likely navigated
  // directly to /workflow/upload — redirect them back.
  // Exception: if we're resuming from ?workflowId= param, show a loader.
  if (!data.workflowId && !uploadComplete) {
    if (isResuming || searchWorkflowId) {
      return (
        <div className="max-w-5xl mx-auto text-center py-20">
          <Loader2 className="w-12 h-12 text-accent-500 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Resuming Workflow</h2>
          <p className="text-muted-foreground">Loading your workflow data...</p>
        </div>
      );
    }
    return (
      <div className="max-w-5xl mx-auto text-center py-20">
        <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Workflow Not Initialized</h2>
        <p className="text-muted-foreground mb-6">
          Please configure your summary preferences first, or resume a draft from{' '}
          <Link to="/analyses" className="text-amber-500 hover:text-amber-400 underline font-medium">
            My Analyses
          </Link>.
        </p>
        <Button onClick={() => navigate({ to: '/workflow/summary' })}>
          Go to Summary Preferences
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Upload className="w-10 h-10 text-accent-500" />
          <h1 className="text-4xl font-bold">Upload Documents</h1>
        </div>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Upload all required documents below. Each category accepts multiple
          PDF files that will be processed concurrently for faster analysis.
        </p>
      </div>

      <div className="space-y-6">
        {/* ---------------------------------------------------------------
            External Research Consent — Shown before upload zones so the
            user can opt in/out before processing begins. Decoupled from
            templates: this toggle writes directly to WorkflowData and
            is not affected by template save/load.
            --------------------------------------------------------------- */}
        {!uploadComplete && (
          <Card className="p-5 rounded-2xl border-2 bg-muted/30">
            <div className="flex items-start gap-4">
              <div className="flex items-center pt-0.5">
                <Checkbox
                  id="enable-external-research"
                  checked={data.summaryPreferences?.enableExternalResearch === true}
                  onCheckedChange={(checked) => {
                    if (data.summaryPreferences) {
                      updateData({
                        summaryPreferences: {
                          ...data.summaryPreferences,
                          enableExternalResearch: checked === true,
                        },
                      });
                    }
                  }}
                  disabled={isUploading}
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="enable-external-research" className="text-base font-semibold flex items-center gap-2 cursor-pointer">
                  <Search className="h-4 w-4" />
                  Enable External Due Diligence Research
                </Label>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  When enabled, an AI-powered research agent will conduct external web searches to
                  verify business legitimacy, screen for adverse media, assess industry outlook, and
                  check principal backgrounds. This enhances report quality with real-time public
                  information.
                </p>
                <div className="flex items-start gap-2 mt-3 p-3 bg-background border rounded-lg">
                  <ShieldAlert className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    <span className="font-semibold text-foreground">Data Disclosure Notice:</span>{' '}
                    By enabling this feature, you acknowledge and consent that business-identifying
                    information — including the company name, principal/owner names, industry
                    classification, and operating location — will be transmitted to third-party AI
                    and web search services to conduct external research. This information is derived
                    from the documents you have uploaded. You are responsible for ensuring you have
                    the necessary authorization to share this information for due diligence purposes.
                    CreditNova does not retain or control data processed by third-party services.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* ---------------------------------------------------------------
            Upload Zones — One per required document type.

            Each zone is a DocumentUploadZone component that handles
            drag-and-drop, file picker, period labels, and file removal.
            Zones are rendered in the same order as requiredTypes:
              financial_statement → credit_report → bank_statement → corporate_search
            --------------------------------------------------------------- */}
        {!uploadComplete && requiredTypes.map((type) => (
          <DocumentUploadZone
            key={type}
            documentType={type}
            files={filesByType[type]}
            onFilesChange={(files) => handleFilesChange(type, files)}
            requirePeriod={type === 'bank_statement'}
            maxFiles={MAX_FILES_PER_ZONE}
            disabled={isUploading}
          />
        ))}

        {/* ---------------------------------------------------------------
            Validation Summary & Submit Button

            Shows file counts per zone, validation warnings, and the
            "Process All Documents" button. Disabled until all zones
            have at least one file and required period labels are filled.
            --------------------------------------------------------------- */}
        {!uploadComplete && requiredTypes.length > 0 && (
          <Card className="p-6 rounded-2xl">
            {/* Per-zone file count summary */}
            <div className="flex flex-wrap gap-4 mb-6">
              {requiredTypes.map((type) => {
                const count = filesByType[type].length;
                const hasFiles = count > 0;
                // Human-readable labels for each document type
                const typeLabels: Record<DocumentType, string> = {
                  financial_statement: 'Financial',
                  bank_statement: 'Bank',
                  credit_report: 'Credit Report',
                  corporate_search: 'Corporate',
                };

                return (
                  <div
                    key={type}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      hasFiles
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                        : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {hasFiles ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <AlertCircle className="w-4 h-4" />
                    )}
                    <span>
                      {typeLabels[type]}: {count} file{count !== 1 ? 's' : ''}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Validation warning for missing files */}
            {!allZonesHaveFiles && totalFileCount > 0 && (
              <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>
                  Please upload at least one file in each document category
                  before processing.
                </p>
              </div>
            )}

            {/* Validation warning for missing bank statement periods */}
            {hasMissingPeriods && (
              <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>
                  Please provide a period label for each bank statement
                  (e.g., &quot;Jan 2025&quot;, &quot;Feb 2025&quot;).
                </p>
              </div>
            )}

            {/* Submit button */}
            <div className="flex justify-end">
              <Button
                size="lg"
                onClick={() => {
                  if (data.summaryPreferences?.enableExternalResearch) {
                    setShowResearchConfirm(true);
                  } else {
                    handleProcessAll();
                  }
                }}
                disabled={
                  isUploading ||
                  !allZonesHaveFiles ||
                  hasMissingPeriods ||
                  totalFileCount === 0
                }
                className="min-w-[220px]"
              >
                {isUploading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Uploading {totalFileCount} file{totalFileCount !== 1 ? 's' : ''}...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Process All Documents ({totalFileCount})
                  </span>
                )}
              </Button>
            </div>
          </Card>
        )}

        {/* ---------------------------------------------------------------
            Processing Dashboard — Two-column layout with live activity panel.

            Left column: per-document extraction status with checkmarks.
            Right column: scrolling live activity log with timestamps and
            source agents (e.g., azure.di, extractor.fs, worker).

            Events arrive via SSE from the backend worker + extraction
            services through Redis Pub/Sub.
            --------------------------------------------------------------- */}
        {uploadComplete && (
          <div className="space-y-6">
            {/* Header Bar */}
            <Card className="p-6 rounded-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    Extraction
                  </p>
                  <h2 className="text-2xl font-bold">
                    {pipelinePhase === 'error' ? (
                      'Processing failed'
                    ) : (
                      <>Analysis <em>in progress</em></>
                    )}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {pipelinePhase === 'error'
                      ? pipelineError || 'An error occurred'
                      : pipelinePhase === 'extracting_fs'
                      ? 'Extracting financial statement data for review...'
                      : pipelinePhase === 'awaiting_review'
                      ? 'Financial data ready — navigating to review...'
                      : `${documentStatuses.length} documents, processing concurrently.`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {pipelinePhase !== 'error' && (
                    <Loader2 className="w-5 h-5 text-accent-500 animate-spin" />
                  )}
                  {pipelinePhase === 'error' && (
                    <XCircle className="w-6 h-6 text-red-500" />
                  )}
                  <span className="text-sm font-medium text-muted-foreground">
                    {documentStatuses.filter(d => d.status === 'completed').length}/{documentStatuses.length} docs
                  </span>
                </div>
              </div>

              {/* Overall Progress Bar */}
              {pipelinePhase !== 'error' && documentStatuses.length > 0 && (
                <div className="mt-4">
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 bg-accent-500"
                      style={{
                        width: pipelinePhase === 'awaiting_review'
                          ? '50%'
                          : pipelinePhase === 'extracting_fs'
                          ? '40%'
                          : `${documentStatuses.length > 0 ? (documentStatuses.filter(d => d.status === 'completed' || d.status === 'failed').length / documentStatuses.length) * 35 : 0}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </Card>

            {/* Two-Column Layout: Documents + Activity Log */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column: Per-Document Extraction Status */}
              <Card className="p-6 rounded-2xl">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                  Per-Document Extraction
                </h3>
                <div className="space-y-2">
                  {documentStatuses.map((doc) => {
                    const integrity = doc.integrityReport;
                    const isExpanded = expandedIntegrity.has(doc.id);
                    const toggleExpand = () => {
                      setExpandedIntegrity((prev) => {
                        const next = new Set(prev);
                        if (next.has(doc.id)) next.delete(doc.id);
                        else next.add(doc.id);
                        return next;
                      });
                    };

                    return (
                      <div key={doc.id}>
                        <div className="flex items-center gap-3 py-2">
                          {doc.status === 'completed' ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                          ) : doc.status === 'failed' ? (
                            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                          ) : doc.status === 'processing' ? (
                            <Loader2 className="w-5 h-5 text-accent-500 animate-spin flex-shrink-0" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              'text-sm font-medium truncate',
                              doc.status === 'failed' && 'text-red-500',
                            )}>
                              {doc.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {doc.status === 'completed' ? 'extracted · source-traced'
                                : doc.status === 'failed' ? 'failed'
                                : doc.status === 'processing' ? 'processing...'
                                : 'queued'}
                            </p>
                          </div>
                          {/* Integrity Badge */}
                          {integrity && doc.status === 'completed' && (
                            <button
                              type="button"
                              onClick={toggleExpand}
                              className={cn(
                                'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 transition-colors',
                                integrity.verdict === 'clean' && 'bg-emerald-500/10 text-emerald-600',
                                integrity.verdict === 'warning' && 'bg-amber-500/10 text-amber-600',
                                integrity.verdict === 'alert' && 'bg-red-500/10 text-red-600',
                              )}
                            >
                              {integrity.verdict === 'clean' ? (
                                <ShieldCheck className="w-3.5 h-3.5" />
                              ) : integrity.verdict === 'warning' ? (
                                <AlertTriangle className="w-3.5 h-3.5" />
                              ) : (
                                <ShieldAlert className="w-3.5 h-3.5" />
                              )}
                              {integrity.verdict === 'clean'
                                ? 'clean'
                                : `${integrity.flags.length} flag${integrity.flags.length !== 1 ? 's' : ''}`}
                              {integrity.flags.length > 0 && (
                                isExpanded
                                  ? <ChevronDown className="w-3 h-3" />
                                  : <ChevronRight className="w-3 h-3" />
                              )}
                            </button>
                          )}
                        </div>

                        {/* Expandable Integrity Flags Panel */}
                        {isExpanded && integrity && integrity.flags.length > 0 && (
                          <div className="ml-8 mb-2 p-3 rounded-lg bg-muted/50 border border-border/50 space-y-1.5">
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              Confidence: {(integrity.avgConfidence * 100).toFixed(1)}%
                            </p>
                            {integrity.flags.map((flag, idx) => (
                              <div
                                key={idx}
                                className={cn(
                                  'flex items-start gap-2 text-xs leading-relaxed',
                                  flag.severity === 'high' && 'text-red-500',
                                  flag.severity === 'medium' && 'text-amber-500',
                                  flag.severity === 'low' && 'text-muted-foreground',
                                )}
                              >
                                <span className="flex-shrink-0 mt-0.5">
                                  {flag.severity === 'high' ? '●' : flag.severity === 'medium' ? '◐' : '○'}
                                </span>
                                <span>{flag.message}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {documentStatuses.length === 0 && (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      Waiting for document status...
                    </p>
                  )}
                </div>
              </Card>

              {/* Right Column: Live Activity Log */}
              <Card className="p-6 rounded-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-4 h-4 text-accent-500" />
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    Agent Activity
                  </h3>
                  {pipelinePhase !== 'error' && pipelinePhase !== 'idle' && (
                    <span className="text-xs text-accent-500 font-medium ml-auto">LIVE</span>
                  )}
                </div>
                <div
                  ref={activityLogRef}
                  className="space-y-1 max-h-[400px] overflow-y-auto font-mono text-xs"
                >
                  {activityLog.map((entry) => {
                    const time = new Date(entry.timestamp);
                    const mm = String(time.getMinutes()).padStart(2, '0');
                    const ss = String(time.getSeconds()).padStart(2, '0');
                    return (
                      <div
                        key={entry.id}
                        className={cn(
                          'flex gap-3 py-1 leading-relaxed',
                          entry.level === 'error' && 'text-red-400',
                          entry.level === 'warning' && 'text-amber-400',
                          entry.level === 'success' && 'text-emerald-400',
                          entry.level === 'info' && 'text-muted-foreground',
                        )}
                      >
                        <span className="text-muted-foreground/60 flex-shrink-0 tabular-nums">
                          {mm}:{ss}
                        </span>
                        <span>
                          <span className="font-semibold">{entry.source}:</span>{' '}
                          {entry.message}
                        </span>
                      </div>
                    );
                  })}
                  {activityLog.length === 0 && (
                    <p className="text-muted-foreground py-4 text-center font-sans text-sm">
                      Waiting for activity...
                    </p>
                  )}
                </div>
              </Card>
            </div>

            {/* Error retry button */}
            {pipelinePhase === 'error' && (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setPipelineError(null);
                    // Retry FS extraction — the only extraction upload.tsx handles.
                    // Remaining extraction + report generation is handled by /workflow/generate.
                    if (hasFinancialDocs && !fsExtractionTriggeredRef.current) {
                      const fsDocs = documentStatuses.filter(
                        (d) => d.documentType === 'financial_statement' && d.status === 'completed'
                      );
                      if (fsDocs.length > 0) {
                        fsExtractionTriggeredRef.current = true;
                        // Clear dedup set for these docs so retry actually re-sends
                        for (const d of fsDocs) extractedDocIdsGlobal.delete(d.id);
                        extractFinancialAndNavigateToReview(fsDocs);
                      }
                    }
                  }}
                >
                  Retry
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---------------------------------------------------------------
          Workflow Navigation — Only shown before the processing pipeline starts.
          Once upload is complete, the pipeline auto-navigates to the report.
          --------------------------------------------------------------- */}
      {!uploadComplete && (
        <WorkflowNavigation
          isLoading={isUploading}
          isNextDisabled={true}
        />
      )}

      {/* ---------------------------------------------------------------
          Research Consent Confirmation Modal

          Shown when the user clicks "Process All Documents" while
          external research is enabled. Displays the data disclosure
          notice and requires explicit confirmation before proceeding.
          --------------------------------------------------------------- */}
      <Dialog
        open={showResearchConfirm}
        onOpenChange={(open) => {
          if (!open) setShowResearchConfirm(false);
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Search className="h-5 w-5 text-amber-500" />
              External Research Enabled
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              Your analysis has the <span className="font-semibold text-foreground">AI Due Diligence Research Agent</span> turned on.
              Before proceeding, please review the data disclosure below.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-2 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg my-2">
            <ShieldAlert className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-sm leading-relaxed">
              <span className="font-semibold">Data Disclosure Notice:</span>{' '}
              By proceeding, you acknowledge and consent that business-identifying
              information — including the company name, principal/owner names, industry
              classification, and operating location — will be transmitted to third-party AI
              and web search services to conduct external research. This information is derived
              from the documents you have uploaded. You are responsible for ensuring you have
              the necessary authorization to share this information for due diligence purposes.
              CreditNova does not retain or control data processed by third-party services.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                // Turn off research and proceed without the modal
                if (data.summaryPreferences) {
                  updateData({
                    summaryPreferences: {
                      ...data.summaryPreferences,
                      enableExternalResearch: false,
                    },
                  });
                }
                setShowResearchConfirm(false);
                handleProcessAll();
              }}
              className="flex-1"
            >
              Disable Research & Continue
            </Button>
            <Button
              onClick={() => {
                setShowResearchConfirm(false);
                handleProcessAll();
              }}
              className="flex-1"
            >
              I Agree — Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
